class FreeGiftPicker extends HTMLElement {
  constructor() {
    super();

    this.products = [];
    this.selectedColorIndex = null;
    this.selectedSize = null;

    this.isAdding = false;
    this.isChanging = false;

    this.delegateClick = this.delegateClick.bind(this);
  }

  connectedCallback() {
    this.removeEventListener('click', this.delegateClick);
    this.addEventListener('click', this.delegateClick);

    this.init();
  }

  disconnectedCallback() {
    this.removeEventListener('click', this.delegateClick);
  }

  init() {
    const dataEl = this.querySelector('[data-free-gift-products]');
    if (!dataEl) return;

    try {
      this.products = JSON.parse(dataEl.textContent);
    } catch (err) {
      console.error('[Free gift] Failed to parse product data', err);
      return;
    }

    if (!Array.isArray(this.products) || this.products.length === 0) return;

    this.selectedColorIndex = null;
    this.selectedSize = null;

    this.renderColors();
    this.updateAddButton();
  }

  delegateClick(event) {
    const target = event.target instanceof Element ? event.target : null;
    if (!target) return;

    const addButton = target.closest('[data-free-gift-add]');

    if (addButton && this.contains(addButton)) {
      event.preventDefault();
      event.stopPropagation();

      if (addButton.disabled || this.isAdding || window.__freeGiftAddInProgress) return;

      return this.handleAddClick();
    }

    const changeButton = target.closest('[data-free-gift-change]');

    if (changeButton && this.contains(changeButton)) {
      event.preventDefault();
      event.stopPropagation();

      if (this.isChanging) return;

      return this.handleChangeClick();
    }
  }

  renderColors() {
    const container = this.querySelector('[data-free-gift-colors]');
    if (!container) return;

    container.innerHTML = '';

    this.products.forEach((product, index) => {
      const variants = Array.isArray(product.variants) ? product.variants : [];
      const hasStock = variants.some((variant) => variant.available);

      const btn = document.createElement('button');

      btn.type = 'button';
      btn.className = 'free-gift-picker__option-value';
      btn.dataset.colorIndex = index;
      btn.setAttribute('aria-pressed', 'false');

      if (!hasStock) {
        btn.setAttribute('disabled', '');
      }

      btn.innerHTML = `<span>${product.color || 'Color ' + (index + 1)}</span>`;
      btn.addEventListener('click', () => this.selectColor(index));

      container.appendChild(btn);
    });
  }

  selectColor(index) {
    const product = this.products[index];
    if (!product) return;

    this.selectedColorIndex = index;

    // No more size step — auto-pick the first in-stock variant for this color
    const variants = Array.isArray(product.variants) ? product.variants : [];
    const availableVariant = variants.find((variant) => variant.available);

    this.selectedSize = availableVariant
      ? { variantId: availableVariant.id, size: availableVariant.size }
      : null;

    this.querySelectorAll('[data-free-gift-colors] .free-gift-picker__option-value').forEach((btn) => {
      btn.setAttribute('aria-pressed', String(parseInt(btn.dataset.colorIndex, 10) === index));
    });

    const heroImage = this.querySelector('[data-free-gift-hero-image]');

    if (heroImage && product.image) {
      heroImage.innerHTML = `<img src="${product.image}" alt="${product.color || ''}" loading="lazy">`;
    }

    const colorLabel = this.querySelector('[data-free-gift-selected-color]');

    if (colorLabel) {
      colorLabel.textContent = product.color || '';
    }

    this.updateAddButton();
  }

  updateAddButton() {
    const addButton = this.querySelector('[data-free-gift-add]');
    if (!addButton) return;

    addButton.disabled = this.isAdding || !(this.selectedColorIndex !== null && this.selectedSize);
  }

  getGiftProductIds() {
    return this.products
      .map((product) => String(product.id))
      .filter(Boolean);
  }

  isClaimedGift(item) {
    return item.properties && String(item.properties._free_gift) === 'true';
  }

  isGiftProduct(item, giftProductIds = this.getGiftProductIds()) {
    return this.isClaimedGift(item) || giftProductIds.includes(String(item.product_id));
  }

  getMinimumSubtotalCents() {
    const minimumSubtotal = Number.parseFloat(this.dataset.minimumSubtotal);

    if (!Number.isFinite(minimumSubtotal) || minimumSubtotal <= 0) {
      return 0;
    }

    return Math.round(minimumSubtotal * 100);
  }

  getSubtotalWithoutGift(cart, giftProductIds = this.getGiftProductIds()) {
    return cart.items.reduce((sum, item) => {
      if (this.isGiftProduct(item, giftProductIds)) return sum;

      return sum + item.final_line_price;
    }, 0);
  }

  async withWatcherSuppressed(callback) {
    const previousValue = window.__freeGiftSuppressWatcher;
    window.__freeGiftSuppressWatcher = true;

    try {
      return await callback();
    } finally {
      window.__freeGiftSuppressWatcher = previousValue;
    }
  }

  async fetchCart() {
    const response = await fetch('/cart.js', {
      headers: {
        Accept: 'application/json',
      },
    });

    if (!response.ok) {
      throw new Error('Cart fetch failed: ' + response.status);
    }

    return response.json();
  }

  async updateLineQuantities(updates) {
    if (!updates || Object.keys(updates).length === 0) {
      return null;
    }

    const response = await this.withWatcherSuppressed(() =>
      fetch('/cart/update.js', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Accept: 'application/json',
        },
        body: JSON.stringify({ updates }),
      })
    );

    if (!response.ok) {
      console.warn('[Free gift] Cart update skipped:', response.status, updates);
      return null;
    }

    return response.json();
  }

  async removeAllGiftLines(cart = null) {
    const currentCart = cart || await this.fetchCart();
    const giftProductIds = this.getGiftProductIds();

    const updates = {};

    currentCart.items.forEach((item) => {
      if (this.isGiftProduct(item, giftProductIds)) {
        updates[item.key] = 0;
      }
    });

    const changedCart = await this.updateLineQuantities(updates);

    return Boolean(changedCart);
  }

  async handleAddClick() {
    if (this.isAdding || window.__freeGiftAddInProgress) return;
    if (this.selectedColorIndex === null || !this.selectedSize) return;

    const addButton = this.querySelector('[data-free-gift-add]');
    if (!addButton) return;

    const originalText = addButton.textContent;

    this.isAdding = true;
    window.__freeGiftAddInProgress = true;

    addButton.disabled = true;
    addButton.textContent = 'Adding…';

    try {
      const cart = await this.fetchCart();
      const giftProductIds = this.getGiftProductIds();
      const minimumInCents = this.getMinimumSubtotalCents();

      if (minimumInCents) {
        const subtotalWithoutGift = this.getSubtotalWithoutGift(cart, giftProductIds);

        if (subtotalWithoutGift < minimumInCents) {
          throw new Error('Cart no longer qualifies for free gift');
        }
      }

      await this.removeAllGiftLines(cart);

      const response = await this.withWatcherSuppressed(() =>
        fetch('/cart/add.js', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Accept: 'application/json',
          },
          body: JSON.stringify({
            items: [
              {
                id: this.selectedSize.variantId,
                quantity: 1,
                properties: {
                  _free_gift: 'true',
                },
              },
            ],
          }),
        })
      );

      if (!response.ok) {
        const errorText = await response.text();
        throw new Error('Add to cart failed: ' + response.status + ' ' + errorText);
      }

      if (window.FreeGiftEnforcer) {
        await window.FreeGiftEnforcer.enforce('after gift add', { refresh: false });
      }

      await this.refreshCartDrawer();
    } catch (err) {
      console.error('[Free gift] Add failed', err);

      addButton.disabled = false;
      addButton.textContent = originalText;

      alert('Sorry, we couldn’t add your free gift. Please try again.');
    } finally {
      this.isAdding = false;
      window.__freeGiftAddInProgress = false;
    }
  }

  async handleChangeClick() {
    const lineKey = this.dataset.claimedLineKey;
    if (!lineKey) return;

    this.isChanging = true;

    try {
      await this.updateLineQuantities({
        [lineKey]: 0,
      });

      await this.refreshCartDrawer();
    } catch (err) {
      console.error('[Free gift] Remove failed', err);
    } finally {
      this.isChanging = false;
    }
  }

  async checkSubtotalAndEnforce(options = {}) {
    if (window.FreeGiftEnforcer) {
      return window.FreeGiftEnforcer.enforce('manual picker check', options);
    }

    return false;
  }

  async refreshCartDrawer() {
    const cartDrawer = document.querySelector('cart-drawer');

    if (!cartDrawer) {
      this.resetAddButton();
      return;
    }

    const wasOpen = cartDrawer.hasAttribute('open');

    try {
      let newCartDrawer = null;

      const section = cartDrawer.closest('.shopify-section');
      const sectionId = section?.id?.replace('shopify-section-', '');

      if (sectionId) {
        const sectionResponse = await fetch(`${window.location.pathname}?section_id=${sectionId}`);

        if (sectionResponse.ok) {
          const sectionHtml = await sectionResponse.text();
          const sectionDoc = new DOMParser().parseFromString(sectionHtml, 'text/html');
          newCartDrawer = sectionDoc.querySelector('cart-drawer');
        }
      }

      if (!newCartDrawer) {
        const response = await fetch(window.location.pathname, {
          headers: {
            'X-Requested-With': 'XMLHttpRequest',
          },
        });

        if (!response.ok) {
          throw new Error('Page fetch failed: ' + response.status);
        }

        const html = await response.text();
        const newDoc = new DOMParser().parseFromString(html, 'text/html');
        newCartDrawer = newDoc.querySelector('cart-drawer');
      }

      if (!newCartDrawer) {
        throw new Error('Could not find cart-drawer in response');
      }

      cartDrawer.innerHTML = newCartDrawer.innerHTML;

      if (wasOpen && !cartDrawer.hasAttribute('open')) {
        cartDrawer.setAttribute('open', '');
      }

      requestAnimationFrame(() => {
        if (wasOpen && !cartDrawer.hasAttribute('open')) {
          cartDrawer.setAttribute('open', '');
        }
      });
    } catch (err) {
      console.error('[Free gift] Refresh failed:', err);
      this.resetAddButton();
    }
  }

  resetAddButton() {
    const addButton = this.querySelector('[data-free-gift-add]');

    if (addButton) {
      addButton.textContent = 'ADD GIFT';
      this.updateAddButton();
    }
  }
}

if (!customElements.get('free-gift-picker')) {
  customElements.define('free-gift-picker', FreeGiftPicker);
}

/**
 * Free gift cart enforcer
 *
 * One cleanup authority:
 * - watches cart AJAX mutations
 * - removes gift when subtotal drops below configured minimum
 * - removes duplicate/orphan gift lines
 * - forces claimed gift quantity back to 1
 */

(() => {
  if (window.__freeGiftCartWatcherInstalled) return;
  window.__freeGiftCartWatcherInstalled = true;



  let enforcementTimer = null;
  let isEnforcing = false;
  let pendingEnforcementReason = null;

  function getGiftProductIds() {
    const dataEl = document.querySelector('[data-free-gift-products]');
    if (!dataEl) return [];

    try {
      const products = JSON.parse(dataEl.textContent);

      if (!Array.isArray(products)) return [];

      return products.map((product) => String(product.id)).filter(Boolean);
    } catch (err) {
      console.error('[Free gift watcher] Failed to parse gift products', err);
      return [];
    }
  }

  function isClaimedFreeGift(item) {
    return item.properties && String(item.properties._free_gift) === 'true';
  }

  function isKnownGiftProduct(item, giftProductIds) {
    return giftProductIds.includes(String(item.product_id));
  }

  function isGiftLine(item, giftProductIds) {
    return isClaimedFreeGift(item) || isKnownGiftProduct(item, giftProductIds);
  }

  function getMinimumSubtotalCents() {
    const picker = document.querySelector('free-gift-picker');

    const minimumSubtotal = Number.parseFloat(
      picker?.dataset?.minimumSubtotal ?? window.__freeGiftMinimumSubtotal ?? 0
    );

    if (!Number.isFinite(minimumSubtotal) || minimumSubtotal <= 0) {
      return 0;
    }

    return Math.round(minimumSubtotal * 100);
  }

  function getCartMutationPath(input) {
    const rawUrl = typeof input === 'string'
      ? input
      : input?.url || '';

    if (!rawUrl) return '';

    try {
      return new URL(rawUrl, window.location.origin).pathname;
    } catch (err) {
      return rawUrl;
    }
  }

  function isCartMutationPath(pathname) {
    return (
      pathname.endsWith('/cart/add.js') ||
      pathname.endsWith('/cart/change.js') ||
      pathname.endsWith('/cart/update.js') ||
      pathname.endsWith('/cart/clear.js')
    );
  }

  async function fetchCart() {
    const response = await fetch('/cart.js', {
      headers: {
        Accept: 'application/json',
      },
    });

    if (!response.ok) {
      throw new Error('Cart fetch failed: ' + response.status);
    }

    return response.json();
  }

  async function updateLineQuantities(updates) {
    if (!updates || Object.keys(updates).length === 0) {
      return null;
    }

    const response = await fetch('/cart/update.js', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Accept: 'application/json',
      },
      body: JSON.stringify({ updates }),
    });

    if (!response.ok) {
      console.warn('[Free gift watcher] Cart update skipped:', response.status, updates);
      return null;
    }

    return response.json();
  }

  async function refreshCartDrawer() {
    const cartDrawer = document.querySelector('cart-drawer');
    if (!cartDrawer) return;

    const wasOpen = cartDrawer.hasAttribute('open');

    try {
      let newCartDrawer = null;

      const section = cartDrawer.closest('.shopify-section');
      const sectionId = section?.id?.replace('shopify-section-', '');

      if (sectionId) {
        const sectionResponse = await fetch(`${window.location.pathname}?section_id=${sectionId}`);

        if (sectionResponse.ok) {
          const sectionHtml = await sectionResponse.text();
          const sectionDoc = new DOMParser().parseFromString(sectionHtml, 'text/html');
          newCartDrawer = sectionDoc.querySelector('cart-drawer');
        }
      }

      if (!newCartDrawer) {
        const response = await fetch(window.location.pathname, {
          headers: {
            'X-Requested-With': 'XMLHttpRequest',
          },
        });

        if (!response.ok) {
          throw new Error('Page fetch failed: ' + response.status);
        }

        const html = await response.text();
        const newDoc = new DOMParser().parseFromString(html, 'text/html');
        newCartDrawer = newDoc.querySelector('cart-drawer');
      }

      if (!newCartDrawer) {
        throw new Error('Could not find cart-drawer in response');
      }

      cartDrawer.innerHTML = newCartDrawer.innerHTML;

      if (wasOpen && !cartDrawer.hasAttribute('open')) {
        cartDrawer.setAttribute('open', '');
      }

      requestAnimationFrame(() => {
        if (wasOpen && !cartDrawer.hasAttribute('open')) {
          cartDrawer.setAttribute('open', '');
        }
      });
    } catch (err) {
      console.error('[Free gift watcher] Cart drawer refresh failed', err);
    }
  }

  async function enforceFreeGiftRules(reason = 'unknown', options = {}) {
    const { refresh = true } = options;

    if (isEnforcing) {
      pendingEnforcementReason = reason;
      return false;
    }

    isEnforcing = true;

    try {
      const cart = await fetchCart();
      const giftProductIds = getGiftProductIds();
      const minimumInCents = getMinimumSubtotalCents();

      const giftLineItems = cart.items.filter((item) =>
        isGiftLine(item, giftProductIds)
      );

      if (!giftLineItems.length) {
        return false;
      }

      const subtotalWithoutGift = cart.items.reduce((sum, item) => {
        if (isGiftLine(item, giftProductIds)) return sum;

        return sum + item.final_line_price;
      }, 0);

      const updates = {};

      if (minimumInCents && subtotalWithoutGift < minimumInCents) {
        giftLineItems.forEach((item) => {
          updates[item.key] = 0;
        });

        console.log('[Free gift watcher] Removed free gift because subtotal dropped below the configured minimum', {
          reason,
          subtotalWithoutGift,
          minimumInCents,
        });
      } else {
        const claimedGifts = giftLineItems.filter(isClaimedFreeGift);
        const orphanGifts = giftLineItems.filter((item) => !isClaimedFreeGift(item));

        orphanGifts.forEach((item) => {
          updates[item.key] = 0;
        });

        claimedGifts.forEach((item, index) => {
          if (index > 0) {
            updates[item.key] = 0;
          }
        });

        const firstClaimedGift = claimedGifts[0];

        if (firstClaimedGift && firstClaimedGift.quantity !== 1) {
          updates[firstClaimedGift.key] = 1;
        }
      }

      if (Object.keys(updates).length === 0) {
        return false;
      }

      const changedCart = await updateLineQuantities(updates);
      const madeChanges = Boolean(changedCart);

      if (madeChanges && refresh) {
        await refreshCartDrawer();
      }

      return madeChanges;
    } catch (err) {
      console.error('[Free gift watcher] Enforcement failed', err);
      return false;
    } finally {
      isEnforcing = false;

      if (pendingEnforcementReason) {
        const nextReason = pendingEnforcementReason;
        pendingEnforcementReason = null;

        enforceFreeGiftRules(nextReason);
      }
    }
  }

  function scheduleFreeGiftEnforcement(reason, delay = 150) {
    clearTimeout(enforcementTimer);

    enforcementTimer = setTimeout(() => {
      enforceFreeGiftRules(reason);
    }, delay);
  }

  function isGiftNoteRequest(init) {
    const headers = init && init.headers;
    if (!headers) return false;
    if (headers instanceof Headers) return headers.get('X-Mediclo-Gift-Note') === '1';
    return headers['X-Mediclo-Gift-Note'] === '1';
  }

  const originalFetch = window.fetch;

  window.fetch = async function (...args) {
    const pathname = getCartMutationPath(args[0]);

    const response = await originalFetch.apply(this, args);

    if (
      isCartMutationPath(pathname) &&
      !isEnforcing &&
      !window.__freeGiftSuppressWatcher &&
      !isGiftNoteRequest(args[1])
    ) {
      enforceFreeGiftRules(pathname);
    }

    return response;
  };

  document.addEventListener(
    'click',
    (event) => {
      const target = event.target instanceof Element ? event.target : null;
      if (!target) return;

      const cartAction = target.closest(
        'cart-drawer button, cart-drawer a, cart-drawer [data-cart-remove], cart-drawer quantity-input, cart-drawer quantity-selector'
      );

      if (cartAction) {
        scheduleFreeGiftEnforcement('cart drawer click', 200);
      }
    },
    true
  );

  document.addEventListener(
    'change',
    (event) => {
      const target = event.target instanceof Element ? event.target : null;
      if (!target) return;

      const cartInput = target.closest(
        'cart-drawer input[name="updates[]"], cart-drawer input[type="number"], cart-drawer select'
      );

      if (cartInput) {
        scheduleFreeGiftEnforcement('cart drawer input change', 200);
      }
    },
    true
  );

  window.FreeGiftEnforcer = {
    enforce: enforceFreeGiftRules,
    schedule: scheduleFreeGiftEnforcement,
    refreshCartDrawer,
  };

  window.freeGiftEnforceNow = () => enforceFreeGiftRules('manual console run');

  scheduleFreeGiftEnforcement('initial load', 100);
})();





/**
 * Free gift checkout guard
 *
 * Purpose:
 * Before allowing the normal cart checkout button to submit,
 * run the free gift cleanup first.
 *
 * If the cart had to be changed, stop checkout and make customer click again.
 */

(() => {
  if (window.__freeGiftCheckoutGuardInstalled) return;
  window.__freeGiftCheckoutGuardInstalled = true;

  let isCheckingCheckout = false;

  function isCheckoutSubmit(form, submitter) {
    if (!form || !(form instanceof HTMLFormElement)) return false;

    const formAction = form.getAttribute('action') || '';
    const isCartForm =
      formAction.includes('/cart') ||
      form.closest('cart-drawer') ||
      form.querySelector('[name="checkout"]');

    const isCheckoutButton =
      submitter?.getAttribute?.('name') === 'checkout' ||
      submitter?.matches?.('[name="checkout"]') ||
      form.querySelector('[name="checkout"]');

    return Boolean(isCartForm && isCheckoutButton);
  }

  function setCheckoutButtonsBusy(form, isBusy) {
    const buttons = form.querySelectorAll('[name="checkout"], button[type="submit"], input[type="submit"]');

    buttons.forEach((button) => {
      if (isBusy) {
        button.dataset.originalText = button.textContent || button.value || '';
        button.disabled = true;

        if (button.tagName === 'BUTTON') {
          button.textContent = 'Checking cart…';
        } else if (button.tagName === 'INPUT') {
          button.value = 'Checking cart…';
        }
      } else {
        button.disabled = false;

        if (button.dataset.originalText) {
          if (button.tagName === 'BUTTON') {
            button.textContent = button.dataset.originalText;
          } else if (button.tagName === 'INPUT') {
            button.value = button.dataset.originalText;
          }

          delete button.dataset.originalText;
        }
      }
    });
  }

  async function enforceBeforeCheckout() {
    if (window.GiftNote?.flush) await window.GiftNote.flush();

    if (window.FreeGiftEnforcer?.enforce) {
      return await window.FreeGiftEnforcer.enforce('checkout guard', {
        refresh: true,
      });
    }

    const picker = document.querySelector('free-gift-picker');

    if (picker?.checkSubtotalAndEnforce) {
      return await picker.checkSubtotalAndEnforce({
        refresh: true,
      });
    }

    return false;
  }

  document.addEventListener(
    'submit',
    async (event) => {
      const form = event.target;
      const submitter = event.submitter;

      if (!isCheckoutSubmit(form, submitter)) return;

      if (form.dataset.freeGiftCheckoutValidated === 'true') {
        delete form.dataset.freeGiftCheckoutValidated;
        return;
      }

      event.preventDefault();
      event.stopPropagation();

      if (isCheckingCheckout) return;

      isCheckingCheckout = true;
      setCheckoutButtonsBusy(form, true);

      try {
        const cartWasChanged = await enforceBeforeCheckout();

        if (cartWasChanged) {
          alert('Your cart was updated because the free gift no longer qualifies. Please review your cart and click checkout again.');
          return;
        }

        form.dataset.freeGiftCheckoutValidated = 'true';

        if (form.requestSubmit && submitter instanceof HTMLElement && document.contains(submitter)) {
          if ('disabled' in submitter) submitter.disabled = false;
          form.requestSubmit(submitter);
        } else {
          form.submit();
        }
      } catch (err) {
        console.error('[Free gift checkout guard] Failed before checkout', err);
        alert('Please wait a moment while we update your cart, then try checkout again.');
      } finally {
        isCheckingCheckout = false;

        if (form.dataset.freeGiftCheckoutValidated !== 'true') {
          setCheckoutButtonsBusy(form, false);
        }
      }
    },
    true
  );

  document.addEventListener(
    'click',
    async (event) => {
      const target = event.target instanceof Element ? event.target : null;
      if (!target) return;

      const checkoutLink = target.closest('a[href="/checkout"], a[href^="/checkout?"]');
      if (!checkoutLink) return;

      event.preventDefault();
      event.stopPropagation();

      if (isCheckingCheckout) return;

      isCheckingCheckout = true;

      try {
        const cartWasChanged = await enforceBeforeCheckout();

        if (cartWasChanged) {
          alert('Your cart was updated because the free gift no longer qualifies. Please review your cart and click checkout again.');
          return;
        }

        window.location.href = checkoutLink.href;
      } catch (err) {
        console.error('[Free gift checkout guard] Failed before checkout link', err);
        alert('Please wait a moment while we update your cart, then try checkout again.');
      } finally {
        isCheckingCheckout = false;
      }
    },
    true
  );
})();